import moment from 'moment';
import { Booking, CreateBooking } from '@/models/bookings';
import CommonMixin from '@/core/pages/CommonMixin';
import { bookingStartDateReconstruction } from '@/utils/functions';

const isSameStartDate = (date: string, booking: Booking) =>
  moment(date).utcOffset(0, true).diff(booking.startDate, 'days') === 0;
const isSameEndDate = (date: string, booking: Booking) =>
  moment(date).utcOffset(0, true).diff(booking.endDate, 'days') === 0;

export default CommonMixin.extend({
  data() {
    return {
      allowed_times: [{ from: '00:00', to: '23:59' }],
    };
  },

  computed: {
    allowedHours(): (hour: string) => boolean {
      return (hour: string) =>
        this.allowed_times.some((time) => {
          const from_hour = +time.from.split(':')[0];
          const to_hour = +time.to.split(':')[0];
          return +hour >= from_hour && +hour <= to_hour;
        });
    },
  },

  methods: {
    allowedMinutes(hour: string): (minute: string) => boolean {
      const time = moment().utcOffset(0, true);
      return (minute: string) =>
        this.allowed_times.some((allowed_time) => {
          const from = allowed_time.from.split(':');
          const to = allowed_time.to.split(':');
          const from_time = time
            .clone()
            .hours(+from[0])
            .minutes(+from[1]);
          const to_time = time
            .clone()
            .set('hours', +to[0])
            .set('minutes', +to[1]);
          const time_to_compare = time
            .clone()
            .set('hours', +hour)
            .set('minutes', +minute);
          return time_to_compare.isBetween(from_time, to_time, 'minute', '[]');
        });
    },

    computeStartTimeAllowedTimes(date: string) {
      const bookings: Booking[] = this.$store.state.monthly_bookings;
      if (!bookings) {
        return;
      }

      const edit_booking: number = +this.$store.state.editBooking;
      // Get all the bookings belonging to the selected date.
      const date_bookings = bookings.filter(
        (booking) =>
          booking.id !== edit_booking &&
          moment(date).utcOffset(0, true).isBetween(booking.startDate, booking.endDate, 'day', '[]'),
      );
      if (date_bookings.length === 0) {
        return;
      }

      const updateTimes = (current_time: moment.Moment, booking: Booking) => {
        const latest = this.allowed_times[this.allowed_times.length - 1];
        latest.to = moment(booking.startDate).format('HH:mm:ss.SSS');
        if (isSameEndDate(current_time.toISOString(), booking)) {
          this.allowed_times.push({ from: moment(booking.endDate).format('HH:mm:ss.SSS'), to: '23:59' });
        }
      };

      let current_time = moment(date).utcOffset(0, true).startOf('day');
      date_bookings.forEach((booking) => {
        if (isSameStartDate(current_time.toISOString(), booking)) {
          updateTimes(current_time, booking);
        } else if (isSameEndDate(current_time.toISOString(), booking)) {
          this.allowed_times = [{ from: moment(booking.endDate).format('HH:mm:ss.SSS'), to: '23:59' }];
        } else {
          this.allowed_times = [];
        }
        current_time = moment(booking.endDate).utcOffset(0, true);
      });
    },

    computeEndTimeAllowedTimes(date: string) {
      const bookings: Booking[] = this.$store.state.monthly_bookings;
      if (!bookings) {
        return;
      }

      const edit_booking: number = +this.$store.state.editBooking;
      const current_booking: CreateBooking = this.$store.state.createBooking;
      // Get all the bookings belonging to the selected date.
      const date_bookings = bookings.filter(
        (booking) =>
          booking.id !== edit_booking &&
          moment(date).utcOffset(0, true).isBetween(booking.startDate, booking.endDate, 'day', '[]'),
      );
      if (date_bookings.length === 0) {
        return;
      }

      const isStartDateBefore = (booking: Booking): boolean => {
        return moment(bookingStartDateReconstruction(current_booking)).isBefore(booking.startDate);
      };

      const updateTimes = (current_time: moment.Moment, booking: Booking) => {
        const latest = this.allowed_times[this.allowed_times.length - 1];
        latest.to = moment(booking.startDate).format('HH:mm:ss.SSS');
        if (isSameEndDate(current_time.toISOString(), booking) && isStartDateBefore(booking)) {
          this.allowed_times.push({ from: moment(booking.endDate).format('HH:mm:ss.SSS'), to: '23:59' });
        }
      };

      let current_time = moment(date).utcOffset(0, true).startOf('day');
      for (const booking of date_bookings) {
        if (isSameStartDate(current_time.toISOString(), booking)) {
          updateTimes(current_time, booking);
        } else if (isSameEndDate(current_time.toISOString(), booking)) {
          this.allowed_times = [{ from: moment(booking.endDate).format('HH:mm:ss.SSS'), to: '23:59' }];
        } else {
          this.allowed_times = [];
        }
        current_time = moment(booking.endDate).utcOffset(0, true);
      }

      // Filter out all the bookings having start time before the selected start time.
      this.allowed_times.filter((time) => {
        const to_time = moment()
          .utcOffset(0, true)
          .set('hours', +time.to.split(':')[0])
          .set('minutes', +time.to.split(':')[1]);
        return to_time.isBefore(bookingStartDateReconstruction(current_booking));
      });
      this.allowed_times[0].from = current_booking.startTime || '00:00';
    },

    getFirstAvailableTime(hour: string, minute: string): { hour: string; minute: string } {
      if (this.allowedHours(hour) && this.allowedMinutes(hour)(minute)) {
        return { hour, minute };
      }
      const allowed_time = this.allowed_times[0]?.from;
      return { hour: allowed_time?.split(':')[0] ?? hour, minute: allowed_time?.split(':')[1] ?? minute };
    },
  },
});
